// components/project/economic/ReconciliationCard.jsx
// R.5.2 §1 — Reconciliation card che consuma /api/projects/{id}/sal-contract-summary
// Nessun calcolo lato frontend: rende solo i dati restituiti dal backend.

import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { AlertTriangle, CheckCircle2, XCircle, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { salApi } from '../../../lib/api';

const formatCurrency = (v) => {
  const n0 = Number(v ?? 0);
  const n = Math.round(n0 * 100) === 0 ? 0 : n0;
  return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
};

const STATUS_META = {
  green: {
    label: 'Coerente',
    color: 'emerald',
    icon: CheckCircle2,
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-800',
    ring: 'ring-emerald-100',
    dot: 'bg-emerald-500',
  },
  yellow: {
    label: 'Da verificare',
    color: 'amber',
    icon: AlertTriangle,
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    text: 'text-amber-800',
    ring: 'ring-amber-100',
    dot: 'bg-amber-500',
  },
  red: {
    label: 'Incoerente',
    color: 'red',
    icon: XCircle,
    bg: 'bg-red-50',
    border: 'border-red-300',
    text: 'text-red-800',
    ring: 'ring-red-100',
    dot: 'bg-red-500',
  },
};

const ReconciliationCard = ({ projectId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await salApi.getContractSummary(projectId);
      setSummary(res.data);
      setLastFetch(new Date());
    } catch (e) {
      if (e.response?.status === 403) {
        setError({ code: 403, message: 'Non autorizzato a visualizzare la riconciliazione.' });
      } else {
        setError({ code: e.response?.status || 0, message: e.response?.data?.detail || 'Errore nel caricamento' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Skeleton
  if (loading) {
    return (
      <Card className="border border-[#EDEDED]" data-testid="reconciliation-card-skeleton">
        <CardContent className="p-4">
          <div className="animate-pulse space-y-2">
            <div className="h-4 w-1/3 bg-gray-200 rounded" />
            <div className="h-6 w-1/2 bg-gray-200 rounded" />
            <div className="h-3 w-1/4 bg-gray-200 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Errore API
  if (error) {
    return (
      <Card className="border border-red-200 bg-red-50" data-testid="reconciliation-card-error">
        <CardContent className="p-4 flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-red-800">Riconciliazione contrattuale non disponibile</div>
            <div className="text-xs text-red-700 mt-0.5">
              {error.code === 403
                ? 'Non hai i permessi per visualizzare questi dati.'
                : (error.message || 'Errore imprevisto.')}
            </div>
            {error.code !== 403 && (
              <Button size="sm" variant="outline" onClick={load} className="mt-2" data-testid="reconciliation-card-retry">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                Riprova
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state — se manca completamente reconciliation (progetto senza contratto)
  if (!summary || !summary.reconciliation) {
    return (
      <Card className="border border-[#EDEDED]" data-testid="reconciliation-card-empty">
        <CardContent className="p-4 text-sm text-[#737373]">
          Riconciliazione contrattuale non disponibile per questo progetto (contratto non ancora firmato).
        </CardContent>
      </Card>
    );
  }

  const rec = summary.reconciliation;
  const status = rec.status || 'green';
  const meta = STATUS_META[status] || STATUS_META.green;
  const StatusIcon = meta.icon;
  const shouldExpand = expanded || status !== 'green';

  return (
    <Card
      className={`border ${meta.border} ${meta.bg} transition-shadow ring-2 ${meta.ring}`}
      data-testid="reconciliation-card"
      data-status={status}
    >
      <CardContent className="p-4">
        {/* Header sempre visibile */}
        <div
          className="flex items-start gap-3 cursor-pointer"
          role="button"
          tabIndex={0}
          onClick={() => setExpanded((v) => !v)}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setExpanded((v) => !v)}
          data-testid="reconciliation-card-header"
        >
          <StatusIcon className={`w-5 h-5 ${meta.text} mt-0.5`} data-testid="reconciliation-status-icon" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className={`text-sm font-bold uppercase tracking-wide ${meta.text}`}>
                Riconciliazione contrattuale
              </h4>
              <span
                className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full text-white ${meta.dot}`}
                data-testid="reconciliation-status-badge"
              >
                {meta.label}
              </span>
            </div>
            <div className="mt-1 text-sm flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className={`${meta.text}`} data-testid="reconciliation-deviation-display">
                Scostamento: <strong>{formatCurrency(rec.deviation)}</strong>
                {rec.deviation_pct != null && (
                  <span className="text-xs opacity-75"> ({Number(rec.deviation_pct).toFixed(2)}%)</span>
                )}
              </span>
              {lastFetch && (
                <span className="text-xs text-[#737373]" data-testid="reconciliation-last-update">
                  Aggiornato: {lastFetch.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          </div>

          <button
            className={`inline-flex items-center gap-1 text-xs ${meta.text} hover:underline`}
            data-testid="reconciliation-card-toggle"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            {shouldExpand ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {shouldExpand ? 'Riduci' : (status === 'green' ? 'Dettaglio' : 'Verifica scostamento')}
          </button>
        </div>

        {/* Breakdown espandibile */}
        {shouldExpand && (
          <div
            className="mt-4 pt-4 border-t border-[#EDEDED] grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm"
            data-testid="reconciliation-breakdown"
          >
            <BreakdownRow label="Contratto iniziale" value={rec.breakdown?.initial_contract_value} testId="rec-initial" />
            <BreakdownRow label="Varianti positive" value={rec.breakdown?.positive_variations} sign="+" positive testId="rec-positive" />
            <BreakdownRow label="Varianti negative" value={rec.breakdown?.negative_variations_other_than_stralci} sign="−" negative testId="rec-negative" />
            <BreakdownRow label="Stralci standalone" value={rec.breakdown?.standalone_stralci} sign="−" negative testId="rec-standalone-stralci" />
            <BreakdownRow label="Recepiti in variante (info)" value={rec.breakdown?.represented_by_variation} muted testId="rec-represented" />
            <BreakdownRow label="Senza effetto contrattuale (info)" value={rec.breakdown?.no_contract_effect} muted testId="rec-no-effect" />
            <BreakdownRow label="Valore ricostruito" value={rec.breakdown?.current_contract_value_computed} bold testId="rec-computed" />
            <BreakdownRow label="Valore contrattuale autoritativo" value={rec.breakdown?.current_contract_value_authoritative} bold testId="rec-authoritative" />
            <BreakdownRow label="Deviazione" value={rec.deviation} bold className={meta.text} testId="rec-deviation" />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const BreakdownRow = ({ label, value, sign, positive, negative, muted, bold, className, testId }) => (
  <div
    className={`flex items-baseline justify-between gap-3 border-b border-dashed border-[#EDEDED] pb-1 last:border-b-0 ${className || ''}`}
    data-testid={testId}
  >
    <span className={`text-xs ${muted ? 'text-[#9CA3AF]' : 'text-[#4B5563]'}`}>{label}</span>
    <span
      className={[
        'font-mono text-sm',
        bold ? 'font-semibold' : '',
        positive ? 'text-emerald-700' : '',
        negative ? 'text-red-700' : '',
        muted ? 'text-[#9CA3AF]' : '',
      ].filter(Boolean).join(' ')}
    >
      {sign && value ? `${sign} ` : ''}{formatCurrency(value)}
    </span>
  </div>
);

export default ReconciliationCard;
